import { useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { ScrollText, RefreshCw, ChevronLeft, ChevronRight, ArrowUp, ArrowDown, ArrowUpDown, Download, X } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { AuditLogRetentionPanel } from "./AuditLogRetentionPanel";

type AuditLog = {
  id: string;
  user_id: string;
  action: string;
  details: Record<string, unknown>;
  created_at: string;
  target_table: string | null;
  target_id: string | null;
};

const PAGE_SIZE_OPTIONS = [25, 50, 100, 200];

type SortKey = "created_at" | "user_id" | "action";

export function AuditLogsTab() {
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [userId, setUserId] = useState("");
  const [action, setAction] = useState("");
  const [targetTable, setTargetTable] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [total, setTotal] = useState<number | null>(null);
  const [sortKey, setSortKey] = useState<SortKey>("created_at");
  const [sortAsc, setSortAsc] = useState(false);

  const load = async (opts?: { page?: number; size?: number; sortKey?: SortKey; sortAsc?: boolean }) => {
    const p = opts?.page ?? page;
    const sz = opts?.size ?? pageSize;
    const sk = opts?.sortKey ?? sortKey;
    const sa = opts?.sortAsc ?? sortAsc;
    setLoading(true);
    let q = supabase
      .from("admin_audit_logs")
      .select("id, user_id, action, details, created_at, target_table, target_id", { count: "exact" })
      .order(sk, { ascending: sa })
      .range(p * sz, p * sz + sz - 1);

    if (userId.trim()) q = q.eq("user_id", userId.trim());
    if (action.trim()) q = q.ilike("action", `%${action.trim()}%`);
    if (targetTable.trim()) q = q.eq("target_table", targetTable.trim());
    if (from) q = q.gte("created_at", new Date(from).toISOString());
    if (to) q = q.lte("created_at", new Date(to).toISOString());

    const { data, error, count } = await q;
    if (error) {
      toast.error("Failed to load audit logs", { description: error.message });
    } else {
      setLogs((data as AuditLog[]) ?? []);
      setTotal(count ?? null);
    }
    setLoading(false);
  };

  useEffect(() => {
    load({ page: 0 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyFilters = () => {
    setPage(0);
    load({ page: 0 });
  };

  const reset = () => {
    setUserId("");
    setAction("");
    setTargetTable("");
    setFrom("");
    setTo("");
    setPage(0);
    setTimeout(() => load({ page: 0 }), 0);
  };

  const goTo = (next: number) => {
    setPage(next);
    load({ page: next });
  };

  const changeSize = (sz: number) => {
    setPageSize(sz);
    setPage(0);
    load({ page: 0, size: sz });
  };

  const [exporting, setExporting] = useState(false);
  const [exportFetched, setExportFetched] = useState(0);
  const [exportTotal, setExportTotal] = useState<number | null>(null);
  const [exportEtaMs, setExportEtaMs] = useState<number | null>(null);
  const cancelExportRef = useRef(false);

  const cancelExport = () => {
    cancelExportRef.current = true;
  };

  const buildFilteredQuery = (
    select: string,
    options?: { count?: "exact" | "planned" | "estimated" }
  ) => {
    let q = supabase
      .from("admin_audit_logs")
      .select(select, options ? { count: options.count } : undefined);
    if (userId.trim()) q = q.eq("user_id", userId.trim());
    if (action.trim()) q = q.ilike("action", `%${action.trim()}%`);
    if (targetTable.trim()) q = q.eq("target_table", targetTable.trim());
    if (from) q = q.gte("created_at", new Date(from).toISOString());
    if (to) q = q.lte("created_at", new Date(to).toISOString());
    return q;
  };

  const exportCsv = async () => {
    setExporting(true);
    setExportFetched(0);
    setExportTotal(null);
    setExportEtaMs(null);
    cancelExportRef.current = false;
    const startedAt = performance.now();
    try {
      const CHUNK = 1000;
      const MAX_ROWS = 50000;

      // Get total count first to drive progress + ETA
      const { count: countRaw } = await buildFilteredQuery("id", { count: "exact" })
        .limit(1);
      const totalAvailable = Math.min(countRaw ?? MAX_ROWS, MAX_ROWS);
      setExportTotal(totalAvailable);

      if (cancelExportRef.current) throw new Error("__cancelled__");

      const rows: AuditLog[] = [];
      let offset = 0;
      while (offset < MAX_ROWS) {
        if (cancelExportRef.current) throw new Error("__cancelled__");
        const { data, error } = await buildFilteredQuery(
          "id, user_id, action, details, created_at, target_table, target_id"
        )
          .order(sortKey, { ascending: sortAsc })
          .range(offset, offset + CHUNK - 1);
        if (error) throw error;
        if (cancelExportRef.current) throw new Error("__cancelled__");
        const batch = (data as unknown as AuditLog[]) ?? [];
        rows.push(...batch);
        const fetched = rows.length;
        setExportFetched(fetched);

        // ETA based on average time per row so far
        const elapsed = performance.now() - startedAt;
        const remaining = Math.max(0, totalAvailable - fetched);
        if (fetched > 0 && remaining > 0) {
          setExportEtaMs((elapsed / fetched) * remaining);
        } else {
          setExportEtaMs(0);
        }

        if (batch.length < CHUNK) break;
        offset += CHUNK;
      }

      // Final guard: if cancelled during the last fetch, do not build a Blob,
      // create an object URL, or trigger a download.
      if (cancelExportRef.current) throw new Error("__cancelled__");

      const escape = (v: unknown) => {
        const s = v == null ? "" : typeof v === "string" ? v : JSON.stringify(v);
        return `"${s.replace(/"/g, '""')}"`;
      };
      const header = ["created_at", "user_id", "action", "target_table", "target_id", "details", "id"];
      const csv = [
        header.join(","),
        ...rows.map((r) =>
          [r.created_at, r.user_id, r.action, r.target_table ?? "", r.target_id ?? "", r.details, r.id].map(escape).join(",")
        ),
      ].join("\n");

      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `audit-logs-${new Date().toISOString().replace(/[:.]/g, "-")}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${rows.length} log${rows.length === 1 ? "" : "s"}`);
    } catch (e) {
      if (e instanceof Error && e.message === "__cancelled__") {
        toast.info("Export cancelled", {
          description: "No file was downloaded. Fetched rows have been discarded.",
        });
      } else {
        toast.error("Export failed", {
          description: e instanceof Error ? e.message : String(e),
        });
      }
    } finally {
      setExporting(false);
      setExportEtaMs(null);
      setExportFetched(0);
      setExportTotal(null);
      cancelExportRef.current = false;
    }
  };

  const formatEta = (ms: number) => {
    const s = Math.max(0, Math.round(ms / 1000));
    if (s < 60) return `${s}s`;
    const m = Math.floor(s / 60);
    const r = s % 60;
    return `${m}m ${r}s`;
  };
  const exportPct =
    exportTotal && exportTotal > 0
      ? Math.min(100, Math.round((exportFetched / exportTotal) * 100))
      : exporting
      ? 5
      : 0;

  const toggleSort = (key: SortKey) => {
    const nextAsc = sortKey === key ? !sortAsc : key === "created_at" ? false : true;
    setSortKey(key);
    setSortAsc(nextAsc);
    setPage(0);
    load({ page: 0, sortKey: key, sortAsc: nextAsc });
  };

  const SortIcon = ({ k }: { k: SortKey }) =>
    sortKey !== k ? (
      <ArrowUpDown className="size-3 opacity-50" />
    ) : sortAsc ? (
      <ArrowUp className="size-3" />
    ) : (
      <ArrowDown className="size-3" />
    );

  const empty = useMemo(() => !loading && logs.length === 0, [loading, logs]);
  const totalPages = total != null ? Math.max(1, Math.ceil(total / pageSize)) : null;
  const rangeStart = total === 0 ? 0 : page * pageSize + 1;
  const rangeEnd = page * pageSize + logs.length;
  const canPrev = page > 0 && !loading;
  const canNext = !loading && (totalPages != null ? page + 1 < totalPages : logs.length === pageSize);

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <ScrollText className="size-5 text-primary" />
        <h2 className="font-display text-2xl font-bold">Audit logs</h2>
      </div>

      <AuditLogRetentionPanel onPurged={() => load({ page: 0 })} />

      <div className="grid grid-cols-1 md:grid-cols-5 gap-3 p-4 border border-border rounded-md bg-card">
        <div className="space-y-1.5">
          <Label className="text-xs font-mono uppercase">User ID</Label>
          <Input
            placeholder="uuid"
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
          />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs font-mono uppercase">Action</Label>
          <Input
            placeholder="e.g. verifier.cancel"
            value={action}
            onChange={(e) => setAction(e.target.value)}
          />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs font-mono uppercase">Table</Label>
          <Input
            placeholder="e.g. raw_signals"
            value={targetTable}
            onChange={(e) => setTargetTable(e.target.value)}
          />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs font-mono uppercase">From</Label>
          <Input
            type="datetime-local"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
          />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs font-mono uppercase">To</Label>
          <Input
            type="datetime-local"
            value={to}
            onChange={(e) => setTo(e.target.value)}
          />
        </div>
        <div className="md:col-span-5 flex flex-wrap gap-2 justify-end">
          <Button variant="outline" onClick={reset} disabled={loading || exporting}>
            Reset
          </Button>
          <Button variant="outline" onClick={exportCsv} disabled={loading || exporting}>
            <Download className={`size-4 mr-2 ${exporting ? "animate-pulse" : ""}`} />
            {exporting ? "Exporting…" : "Export CSV"}
          </Button>
          {exporting && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="destructive">
                  <X className="size-4 mr-2" />
                  Cancel
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Cancel CSV export?</AlertDialogTitle>
                  <AlertDialogDescription>
                    The export is{" "}
                    {exportTotal != null ? `${exportPct}% complete` : "in progress"}
                    . Cancelling will discard all fetched rows and no file will be
                    downloaded.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Keep exporting</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={cancelExport}
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  >
                    Cancel export
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
          <Button onClick={applyFilters} disabled={loading || exporting}>
            <RefreshCw className={`size-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            Apply filters
          </Button>
        </div>
        {exporting && (
          <div className="md:col-span-5 space-y-1.5" aria-live="polite">
            <Progress value={exportPct} className="h-2" />
            <div className="flex justify-between text-xs font-mono text-muted-foreground">
              <span>
                {exportFetched.toLocaleString()}
                {exportTotal != null ? ` / ${exportTotal.toLocaleString()}` : ""} rows
                {exportTotal != null ? ` (${exportPct}%)` : ""}
              </span>
              <span>
                {exportEtaMs != null && exportEtaMs > 0
                  ? `~${formatEta(exportEtaMs)} remaining`
                  : exportTotal != null
                  ? "Finalizing…"
                  : "Estimating…"}
              </span>
            </div>
          </div>
        )}
      </div>

      <div className="border border-border rounded-md overflow-hidden">
        <div className="grid grid-cols-12 gap-2 px-4 py-2 bg-muted/50 text-xs font-mono uppercase text-muted-foreground">
          <button
            type="button"
            onClick={() => toggleSort("created_at")}
            className="col-span-3 flex items-center gap-1.5 text-left hover:text-foreground transition-colors"
          >
            Time <SortIcon k="created_at" />
          </button>
          <button
            type="button"
            onClick={() => toggleSort("user_id")}
            className="col-span-3 flex items-center gap-1.5 text-left hover:text-foreground transition-colors"
          >
            User <SortIcon k="user_id" />
          </button>
          <button
            type="button"
            onClick={() => toggleSort("action")}
            className="col-span-3 flex items-center gap-1.5 text-left hover:text-foreground transition-colors"
          >
            Action <SortIcon k="action" />
          </button>
          <div className="col-span-3">Details</div>
        </div>
        <div className="divide-y divide-border">
          {empty && (
            <div className="px-4 py-8 text-center text-sm text-muted-foreground">
              No audit logs match the current filters.
            </div>
          )}
          {logs.map((l) => (
            <div
              key={l.id}
              className="grid grid-cols-12 gap-2 px-4 py-3 text-sm items-start"
            >
              <div className="col-span-3 font-mono text-xs">
                {new Date(l.created_at).toLocaleString()}
              </div>
              <div className="col-span-3 font-mono text-xs truncate" title={l.user_id}>
                {l.user_id}
              </div>
              <div className="col-span-3 font-mono text-xs">{l.action}</div>
              <div className="col-span-3">
                <pre className="text-[11px] font-mono text-muted-foreground whitespace-pre-wrap break-all">
                  {JSON.stringify(l.details, null, 0)}
                </pre>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 items-center justify-between text-sm">
        <div className="flex items-center gap-2 text-muted-foreground font-mono text-xs">
          <span>Rows per page</span>
          <Select value={String(pageSize)} onValueChange={(v) => changeSize(Number(v))}>
            <SelectTrigger className="h-8 w-[80px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PAGE_SIZE_OPTIONS.map((n) => (
                <SelectItem key={n} value={String(n)}>
                  {n}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-3 font-mono text-xs text-muted-foreground">
          <span>
            {total != null
              ? `${rangeStart}–${rangeEnd} of ${total}`
              : `${rangeStart}–${rangeEnd}`}
          </span>
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="sm"
              onClick={() => goTo(page - 1)}
              disabled={!canPrev}
            >
              <ChevronLeft className="size-4" />
            </Button>
            <span className="px-2">
              Page {page + 1}
              {totalPages != null ? ` / ${totalPages}` : ""}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => goTo(page + 1)}
              disabled={!canNext}
            >
              <ChevronRight className="size-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
